import { useEffect, useState } from 'react'
import { Box, CircularProgress, Collapse } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FONT_MONO, FONT_SANS, TOKENS } from './OptimiserDesign'
import { RunConfig, RunResult, useRunSolver } from './Optimiser.data'

type PanelState = 'config' | 'running' | 'done' | 'error'

const PHASES: { value: number; sublabel: string; isMilp: boolean }[] = [
  { value: 1, sublabel: 'Basic', isMilp: false },
  { value: 2, sublabel: '+ Service', isMilp: false },
  { value: 3, sublabel: '+ Capacity', isMilp: false },
  { value: 4, sublabel: '+ Commits', isMilp: false },
  { value: 5, sublabel: '+ Materials', isMilp: false },
  { value: 6, sublabel: '+ MOQs', isMilp: true },
]

const MOQ_OPTIONS: { value: RunConfig['moq_enforcement']; label: string }[] = [
  { value: 'hard', label: 'Hard' },
  { value: 'soft', label: 'Soft' },
  { value: 'soft_indicator', label: 'Soft Indicator' },
  { value: 'hard_rounded', label: 'Hard Rounded' },
]

const DEFAULT_CONFIG: RunConfig = {
  phase: 2,
  service_mode: 'lost_sales',
  moq_enforcement: 'hard',
  time_limit: 300,
  mip_gap: 0.01,
}

const fmt = (n: number | null | undefined) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(n)

const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`

const formatElapsed = (s: number) =>
  s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`

// ── Shared primitives ──────────────────────────────────────────────────────

const Label = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: 'block',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: TOKENS.ink300,
      fontFamily: FONT_SANS,
      mb: 1.5,
    }}
  >
    {children}
  </Box>
)

const Chip = ({
  selected,
  onClick,
  children,
  disabled,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
  disabled?: boolean
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: '10px',
      py: '4px',
      borderRadius: '4px',
      fontSize: 12,
      fontWeight: selected ? 700 : 400,
      fontFamily: FONT_SANS,
      cursor: disabled ? 'default' : 'pointer',
      border: '1px solid',
      borderColor: selected ? TOKENS.brandTeal : TOKENS.line300,
      background: selected ? TOKENS.brandTeal : TOKENS.surface0,
      color: selected
        ? TOKENS.surface0
        : disabled
          ? TOKENS.ink300
          : TOKENS.ink700,
      opacity: disabled ? 0.5 : 1,
      userSelect: 'none',
      transition: 'all 0.12s',
      '&:hover': disabled
        ? {}
        : {
            borderColor: TOKENS.brandTeal,
            color: selected ? TOKENS.surface0 : TOKENS.brandTeal,
          },
    }}
  >
    {children}
  </Box>
)

const ChipRow = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: 4 }}>
    {children}
  </Box>
)

const RunButton = ({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: '18px',
      py: '8px',
      borderRadius: '5px',
      fontSize: 13,
      fontWeight: 700,
      fontFamily: FONT_SANS,
      cursor: disabled ? 'default' : 'pointer',
      background: disabled ? TOKENS.line200 : TOKENS.brandTeal,
      color: disabled ? TOKENS.ink300 : TOKENS.surface0,
      userSelect: 'none',
      transition: 'background 0.12s',
      '&:hover': disabled ? {} : { background: TOKENS.teal700 },
    }}
  >
    {children}
  </Box>
)

const OutlineButton = ({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: '14px',
      py: '6px',
      borderRadius: '5px',
      fontSize: 12,
      fontWeight: 600,
      fontFamily: FONT_SANS,
      cursor: 'pointer',
      border: `1px solid ${TOKENS.line300}`,
      background: TOKENS.surface0,
      color: TOKENS.ink700,
      userSelect: 'none',
      '&:hover': { borderColor: TOKENS.brandTeal, color: TOKENS.brandTeal },
    }}
  >
    {children}
  </Box>
)

// ── Config state ───────────────────────────────────────────────────────────

const ConfigView = ({
  config,
  onChange,
  onRun,
}: {
  config: RunConfig
  onChange: (patch: Partial<RunConfig>) => void
  onRun: () => void
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const selectedPhase = PHASES.find((p) => p.value === config.phase)

  return (
    <Box>
      <Label>Phase</Label>
      <ChipRow>
        {PHASES.map((p) => (
          <Chip
            key={p.value}
            selected={config.phase === p.value}
            onClick={() => onChange({ phase: p.value })}
          >
            <Box
              component="span"
              sx={{ fontFamily: FONT_MONO, fontWeight: 700, mr: '5px' }}
            >
              {p.value}
            </Box>
            {p.sublabel}
            {p.isMilp && (
              <Box
                component="span"
                sx={{ ml: '5px', fontSize: 9, opacity: 0.75 }}
              >
                MILP
              </Box>
            )}
          </Chip>
        ))}
      </ChipRow>

      {config.phase === 6 && (
        <Box
          sx={{
            mb: 4,
            p: '10px 12px',
            borderRadius: '4px',
            background: TOKENS.amberTint,
            border: `1px solid ${TOKENS.stateRunning}`,
            fontSize: 12,
            color: TOKENS.amberFg,
            fontFamily: FONT_SANS,
          }}
        >
          Phase 6 introduces binary variables (MILP). Solve time may be
          significantly longer — consider increasing the time limit.
        </Box>
      )}

      <Label>Service Mode</Label>
      <ChipRow>
        <Chip
          selected={config.service_mode === 'lost_sales'}
          onClick={() => onChange({ service_mode: 'lost_sales' })}
        >
          Lost Sales
        </Chip>
        <Chip
          selected={config.service_mode === 'backorder'}
          onClick={() => onChange({ service_mode: 'backorder' })}
        >
          Backorder
        </Chip>
      </ChipRow>

      <Box sx={{ opacity: config.phase === 6 ? 1 : 0.4, mb: 4 }}>
        <Label>
          MOQ Enforcement
          {config.phase !== 6 && (
            <Box
              component="span"
              sx={{
                ml: 2,
                fontSize: 9,
                background: TOKENS.line100,
                color: TOKENS.ink400,
                px: '5px',
                py: '1px',
                borderRadius: '3px',
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              Phase 6 only
            </Box>
          )}
        </Label>
        <ChipRow>
          {MOQ_OPTIONS.map((o) => (
            <Chip
              key={o.value}
              selected={config.moq_enforcement === o.value}
              disabled={config.phase !== 6}
              onClick={() => onChange({ moq_enforcement: o.value })}
            >
              {o.label}
            </Chip>
          ))}
        </ChipRow>
      </Box>

      <Box
        onClick={() => setAdvancedOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          cursor: 'pointer',
          color: TOKENS.ink400,
          fontSize: 11,
          fontWeight: 700,
          fontFamily: FONT_SANS,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          userSelect: 'none',
        }}
      >
        Advanced Settings
        {advancedOpen ? (
          <ExpandLessIcon sx={{ fontSize: 14 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 14 }} />
        )}
      </Box>
      <Collapse in={advancedOpen}>
        <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Label>Time Limit (seconds)</Label>
            <Box
              component="input"
              type="number"
              value={config.time_limit}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ time_limit: Number(e.target.value) })
              }
              sx={{
                width: '100%',
                px: '10px',
                py: '6px',
                border: `1px solid ${TOKENS.line300}`,
                borderRadius: '4px',
                fontSize: 12,
                fontFamily: FONT_MONO,
                color: TOKENS.ink700,
                background: TOKENS.surface0,
                outline: 'none',
                '&:focus': { borderColor: TOKENS.brandTeal },
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Label>MIP Gap</Label>
            <Box
              component="input"
              type="number"
              step="0.001"
              value={config.mip_gap}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ mip_gap: Number(e.target.value) })
              }
              sx={{
                width: '100%',
                px: '10px',
                py: '6px',
                border: `1px solid ${TOKENS.line300}`,
                borderRadius: '4px',
                fontSize: 12,
                fontFamily: FONT_MONO,
                color: TOKENS.ink700,
                background: TOKENS.surface0,
                outline: 'none',
                '&:focus': { borderColor: TOKENS.brandTeal },
              }}
            />
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mt: 2 }}>
        <RunButton onClick={onRun}>▶ Run Solver</RunButton>
        <Box sx={{ fontSize: 11, color: TOKENS.ink300, fontFamily: FONT_SANS }}>
          Phase {config.phase} {selectedPhase?.isMilp ? 'MILP' : 'LP'} ·{' '}
          {config.service_mode === 'lost_sales' ? 'Lost Sales' : 'Backorder'}
        </Box>
      </Box>
    </Box>
  )
}

// ── Running state ──────────────────────────────────────────────────────────

const RunningView = ({ config }: { config: RunConfig }) => {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        py: 16,
      }}
    >
      <CircularProgress
        size={36}
        thickness={3}
        sx={{ color: TOKENS.brandTeal }}
      />
      <Box
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: TOKENS.ink700,
          fontFamily: FONT_SANS,
        }}
      >
        Solving Phase {config.phase} {config.phase === 6 ? 'MILP' : 'LP'}…
      </Box>
      <Box
        sx={{
          fontSize: 12,
          color: TOKENS.ink300,
          fontFamily: FONT_MONO,
        }}
      >
        {formatElapsed(elapsed)} elapsed
      </Box>
    </Box>
  )
}

// ── Results state ──────────────────────────────────────────────────────────

const KpiCard = ({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent?: 'green' | 'red' | 'blue'
}) => {
  const colors = {
    green: {
      bg: TOKENS.stateCompleteTint,
      value: TOKENS.stateCompleteFg,
      label: TOKENS.stateComplete,
    },
    red: {
      bg: TOKENS.stateFailedTint,
      value: TOKENS.stateFailedFg,
      label: TOKENS.stateFailed,
    },
    blue: { bg: TOKENS.blue50, value: TOKENS.brandTeal, label: TOKENS.blue600 },
  }
  const c = colors[accent ?? 'blue']
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 100,
        background: c.bg,
        borderRadius: '6px',
        p: '10px 12px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          fontSize: 18,
          fontWeight: 700,
          fontFamily: FONT_MONO,
          color: c.value,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Box>
      <Box
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: c.label,
          fontFamily: FONT_SANS,
          mt: '3px',
        }}
      >
        {label}
      </Box>
    </Box>
  )
}

const ResultsView = ({
  result,
  onRunAgain,
  onViewOutput,
}: {
  result: RunResult
  onRunAgain: () => void
  onViewOutput: (runId: string, tableName: string) => void
}) => {
  const cb = result.cost_breakdown
  const kpis = result.kpis
  const isOk =
    result.termination_condition === 'optimal' ||
    result.termination_condition === 'feasible'

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          mb: 4,
          pb: 4,
          borderBottom: `1px solid ${TOKENS.line200}`,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isOk ? TOKENS.stateComplete : TOKENS.stateFailed,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: TOKENS.ink700,
            fontFamily: FONT_SANS,
            flex: 1,
          }}
        >
          {isOk ? 'Solve Complete' : 'Solve Failed'} — Phase {result.phase} ·{' '}
          {result.service_mode === 'lost_sales' ? 'Lost Sales' : 'Backorder'}
        </Box>
        <Box
          sx={{
            fontSize: 11,
            color: TOKENS.ink300,
            fontFamily: FONT_MONO,
          }}
        >
          {result.runtime_s != null
            ? formatElapsed(Math.round(result.runtime_s))
            : ''}
        </Box>
      </Box>

      {isOk && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 5, flexWrap: 'wrap' }}>
            <KpiCard
              label="Service Level"
              value={pct(kpis?.service_level)}
              accent="green"
            />
            <KpiCard
              label="Total Cost"
              value={fmt(result.objective)}
              accent="blue"
            />
            <KpiCard label="Landed" value={fmt(cb?.landed)} accent="blue" />
            <KpiCard label="Holding" value={fmt(cb?.holding)} accent="blue" />
            <KpiCard
              label="Svc Penalty"
              value={fmt(cb?.service)}
              accent={cb?.service > 0 ? 'red' : 'blue'}
            />
          </Box>

          {result.outputs.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Label>Output Tables</Label>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {result.outputs.map((t) => (
                  <OutlineButton
                    key={t}
                    onClick={() => onViewOutput(result.run_id, t)}
                  >
                    {t.replace(/_/g, ' ')}
                  </OutlineButton>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      {!isOk && (
        <Box
          sx={{
            mb: 5,
            p: '10px 12px',
            borderRadius: '4px',
            background: TOKENS.stateFailedTint,
            border: `1px solid ${TOKENS.stateFailed}`,
            fontSize: 12,
            color: TOKENS.stateFailedFg,
            fontFamily: FONT_SANS,
          }}
        >
          Termination: {result.termination_condition}. The model may be
          infeasible — check your input data or try a lower phase.
        </Box>
      )}

      <RunButton onClick={onRunAgain}>▶ Run Again</RunButton>
    </Box>
  )
}

// ── Error state ────────────────────────────────────────────────────────────

const ErrorView = ({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      py: 16,
    }}
  >
    <Box
      sx={{
        fontSize: 13,
        color: TOKENS.stateFailedFg,
        fontFamily: FONT_SANS,
        textAlign: 'center',
        maxWidth: 360,
      }}
    >
      {message}
    </Box>
    <OutlineButton onClick={onRetry}>Try Again</OutlineButton>
  </Box>
)

// ── Root component ─────────────────────────────────────────────────────────

interface RunSolverPanelProps {
  onViewOutput: (runId: string, tableName: string) => void
  scenarioId?: string
}

export const RunSolverPanel = ({
  onViewOutput,
  scenarioId,
}: RunSolverPanelProps) => {
  const [panelState, setPanelState] = useState<PanelState>('config')
  const [config, setConfig] = useState<RunConfig>(DEFAULT_CONFIG)
  const [result, setResult] = useState<RunResult | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const mutation = useRunSolver(scenarioId)

  const handleRun = () => {
    setPanelState('running')
    mutation.mutate(config, {
      onSuccess: (data) => {
        setResult(data)
        setPanelState('done')
      },
      onError: (err) => {
        setErrorMsg(err.message ?? 'An unexpected error occurred.')
        setPanelState('error')
      },
    })
  }

  const handleRunAgain = () => {
    setResult(null)
    setPanelState('config')
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: '32px 36px',
        fontFamily: FONT_SANS,
      }}
    >
      <Box
        sx={{
          fontSize: 15,
          fontWeight: 700,
          color: TOKENS.ink900,
          mb: 1,
          fontFamily: FONT_SANS,
        }}
      >
        Run Solver
      </Box>
      <Box
        sx={{
          fontSize: 12,
          color: TOKENS.ink400,
          mb: 6,
          fontFamily: FONT_SANS,
        }}
      >
        Configure and run the supply chain LP/MILP solver against the uploaded
        input data.
      </Box>

      <Box sx={{ maxWidth: 560 }}>
        {panelState === 'config' && (
          <ConfigView
            config={config}
            onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
            onRun={handleRun}
          />
        )}
        {panelState === 'running' && <RunningView config={config} />}
        {panelState === 'done' && result && (
          <ResultsView
            result={result}
            onRunAgain={handleRunAgain}
            onViewOutput={onViewOutput}
          />
        )}
        {panelState === 'error' && (
          <ErrorView message={errorMsg} onRetry={handleRunAgain} />
        )}
      </Box>
    </Box>
  )
}
